import React from 'react';
import { Theme, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useDecision } from '@optimizely/react-sdk';
import { DealTile, DealTileData } from './DealTile';
import { MenuRecipe } from '@/menu/pizza/pizzaMenuTypes';
import formattedPrice from '@/common/formattedPrice';
import DealTileSkeleton from './DealTile.skeleton';
import { useGetNewProducts } from '../../hooks/useGetNewProducts';
import { mobileStartBreakpoint } from '@/materialUi/theme';
import { useQuickAddDeal } from '../../hooks/useQuickAddDeal';
import { useGetQuickAddDeals } from '../../hooks/useGetQuickAddDeals';

type DealTileListProps = {
  deals: MenuRecipe[];
  onDealClick: (deal: MenuRecipe, index?: number) => void;
  isLoading: boolean;
};

type DealTileListStyleProps = {
  isSmallScreen: boolean;
}

const useDealGridStyles = makeStyles<Theme, DealTileListStyleProps>(() => createStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: (props: { isSmallScreen: boolean }) => props.isSmallScreen ? '16px' : '24px'
  }
}));

export function DealTileList({
  deals,
  onDealClick,
  isLoading
}: DealTileListProps): JSX.Element {
  const newProducts = useGetNewProducts();
  const quickAddDeals = useGetQuickAddDeals();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(mobileStartBreakpoint));
  const classes = useDealGridStyles({ isSmallScreen });
  const { quickAddDealToCart, dealIdCurrentlyAddingToCart } = useQuickAddDeal();
  const [{enabled: timerBannerDealTileFeatureEnabled}]= useDecision('fr-dtg-1709-timer-banner-deal-tile');
  const [{enabled: dealsCTACopyChangeBuildDealEnabled}]= useDecision('fr-ims-609-deals-page-cta-copy-change');

  if (isLoading) {
    return (
      <div className={classes.container} key="skelton-grid">
        {Array.from({ length: 8 }, (_, k) => (
          <DealTileSkeleton key={`localized-deal-tile-skeleton-${k}`} />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="deals-localized-list" className={classes.container}>
      {deals.map((deal, index) => {
        const { id, name, imageAltText, productDescription: description, price, outOfStock, imageURL, quickAddPayload, availability } = deal;

        const isDealQuickAddEligible = quickAddPayload && quickAddDeals?.[id];
        const dealBuilderCTAText = dealsCTACopyChangeBuildDealEnabled ? 'Build Deal' : 'Get Started';
        const primaryCTAText = isDealQuickAddEligible ? 'Add to Cart' : dealBuilderCTAText;
          
        const dealData: DealTileData = {
          id,
          name,
          imageAltText,
          description,
          outOfStock,
          imageURL,
          formattedPrice: outOfStock || !price ? null : formattedPrice((price || 0)),
          primaryCTA: {
            text: primaryCTAText,
            onClick: isDealQuickAddEligible 
                        ? () => quickAddDealToCart(quickAddPayload, deal)
                        : () => onDealClick(deal, index),
            isLoading: !!isDealQuickAddEligible && (dealIdCurrentlyAddingToCart === quickAddPayload.id)
          },
          badges: {
            new: newProducts?.[id],
            soldOut: outOfStock
          },
          availability: timerBannerDealTileFeatureEnabled ? availability || [] : undefined
        }

        return (
          <DealTile
            key={`localized-deal-tile-${id}`}
            deal={dealData}
            index={index}
          />
        );
      })}
    </div>
  );
};
